import { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, X, Plus, MapPin, Phone, Mail, MessageSquare,
  Building2, Home, Check, Loader2, Image, ChevronDown, ChevronUp, Maximize
} from 'lucide-react';
import { Button } from './ui/button';
import PriceModePicker from './PriceModePicker';
import { toast } from 'sonner';
import axios from 'axios';
import GoogleMapPicker from './GoogleMapPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROPERTY_AMENITIES = [
  { id: 'pool', label: 'مسبح', labelEn: 'Swimming Pool', icon: '🏊' },
  { id: 'gym', label: 'صالة رياضية', labelEn: 'Gym', icon: '🏋️' },
  { id: 'security', label: 'أمن 24 ساعة', labelEn: '24h Security', icon: '🔒' },
  { id: 'balcony', label: 'بلكونة', labelEn: 'Balcony', icon: '🌅' },
  { id: 'maid_room', label: 'غرفة خادمة', labelEn: 'Maid Room', icon: '🛏️' },
  { id: 'storage', label: 'غرفة تخزين', labelEn: 'Storage Room', icon: '📦' },
  { id: 'central_ac', label: 'تكييف مركزي', labelEn: 'Central A/C', icon: '❄️' },
  { id: 'covered_parking', label: 'موقف مغطى', labelEn: 'Covered Parking', icon: '🅿️' },
  { id: 'pets_allowed', label: 'يسمح بالحيوانات', labelEn: 'Pets Allowed', icon: '🐕' },
  { id: 'sea_view', label: 'إطلالة بحرية', labelEn: 'Sea View', icon: '🌊' },
  { id: 'garden', label: 'حديقة', labelEn: 'Garden', icon: '🌳' },
  { id: 'private_pool', label: 'مسبح خاص', labelEn: 'Private Pool', icon: '🏖️' },
  { id: 'lobby', label: 'لوبي', labelEn: 'Lobby in Building', icon: '🏢' },
  { id: 'concierge', label: 'خدمة كونسيرج', labelEn: 'Concierge', icon: '🛎️' },
  { id: 'children_play', label: 'منطقة ألعاب أطفال', labelEn: 'Children Play Area', icon: '🎠' },
  { id: 'bbq', label: 'منطقة شواء', labelEn: 'BBQ Area', icon: '🍖' },
  { id: 'chiller_free', label: 'التبريد مجاني', labelEn: 'Chiller Free', icon: '💨' },
  { id: 'near_metro', label: 'قريب من المترو', labelEn: 'Near Metro', icon: '🚇' },
];

const EMIRATES = [
  { value: 'abu_dhabi', label: 'أبوظبي', labelEn: 'Abu Dhabi' },
  { value: 'dubai', label: 'دبي', labelEn: 'Dubai' },
  { value: 'sharjah', label: 'الشارقة', labelEn: 'Sharjah' },
  { value: 'ajman', label: 'عجمان', labelEn: 'Ajman' },
  { value: 'ras_al_khaimah', label: 'رأس الخيمة', labelEn: 'Ras Al Khaimah' },
  { value: 'fujairah', label: 'الفجيرة', labelEn: 'Fujairah' },
  { value: 'umm_al_quwain', label: 'أم القيوين', labelEn: 'Umm Al Quwain' }
];

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'شقة', labelEn: 'Apartment' },
  { value: 'villa', label: 'فيلا', labelEn: 'Villa' },
  { value: 'townhouse', label: 'تاون هاوس', labelEn: 'Townhouse' },
  { value: 'penthouse', label: 'بنتهاوس', labelEn: 'Penthouse' },
  { value: 'compound', label: 'مجمع سكني', labelEn: 'Compound' },
  { value: 'duplex', label: 'دوبلكس', labelEn: 'Duplex' },
  { value: 'studio', label: 'استوديو', labelEn: 'Studio' },
  { value: 'land', label: 'أرض', labelEn: 'Land' },
  { value: 'building', label: 'مبنى', labelEn: 'Building' },
  { value: 'commercial', label: 'محل تجاري', labelEn: 'Commercial' },
  { value: 'office', label: 'مكتب', labelEn: 'Office' },
  { value: 'warehouse', label: 'مستودع', labelEn: 'Warehouse' }
];

export default function AddPropertyPage({ store, language, isDarkMode, onBack, onSuccess, editItem = null, isEditMode = false }) {
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState({ basic: true, details: true, building: false, amenities: false, contact: true, location: true });
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  
  // Initialize form with editItem data if in edit mode
  const [form, setForm] = useState(() => {
    if (editItem && isEditMode) {
      return {
        listingType: editItem.listingType || 'for_sale',
        title: editItem.title || '',
        propertyType: editItem.propertyType || 'apartment',
        emirate: editItem.emirate || 'dubai',
        community: editItem.community || '',
        subCommunity: editItem.subCommunity || '',
        buildingName: editItem.buildingName || '',
        floor: editItem.floor || '',
        bedrooms: editItem.bedrooms || '',
        bathrooms: editItem.bathrooms || '',
        area: editItem.area || '',
        plotArea: editItem.plotArea || '',
        parking: editItem.parking || '',
        furnished: editItem.furnished || 'unfurnished',
        price: editItem.price || '',
        priceOnRequest: !!editItem.priceOnRequest,
        ownership: editItem.ownership || 'freehold',
        completion: editItem.completion || 'ready',
        rentPrice: editItem.rentPrice || '',
        cheques: editItem.cheques || '1',
        expectedRoi: editItem.expectedRoi || '',
        totalFloors: editItem.totalFloors || '',
        developer: editItem.developer || '',
        amenities: editItem.amenities || [],
        contactPhone: editItem.contactPhone || store?.phone || '',
        contactWhatsapp: editItem.contactWhatsapp || store?.whatsapp || '',
        contactEmail: editItem.contactEmail || store?.email || '',
        locationAddress: editItem.location || editItem.locationAddress || '',
        mapLat: editItem.latitude || editItem.mapLat || '',
        mapLng: editItem.longitude || editItem.mapLng || '',
        description: editItem.description || '',
        status: editItem.status || 'vacant'
      };
    }
    return {
      listingType: 'for_sale', title: '', propertyType: 'apartment', emirate: 'dubai', community: '', subCommunity: '', buildingName: '', floor: '',
      bedrooms: '', bathrooms: '', area: '', plotArea: '', parking: '', furnished: 'unfurnished',
      price: '', priceOnRequest: false, ownership: 'freehold', completion: 'ready', rentPrice: '', cheques: '1', expectedRoi: '',
      totalFloors: '', developer: '', amenities: [], videoTourUrl: '', tour360Url: '',
      contactPhone: store?.phone || '', contactWhatsapp: store?.whatsapp || '', contactEmail: store?.email || '',
      locationAddress: '', mapLat: '', mapLng: '', description: '', status: 'vacant'
    };
  });

  // Initialize images from editItem if in edit mode
  useEffect(() => {
    if (editItem && isEditMode) {
      const existingImages = editItem.images || (editItem.image ? [editItem.image] : []);
      if (existingImages.length > 0) {
        setImages(existingImages.map(url => ({ preview: url, isExisting: true })));
      }
    }
  }, [editItem, isEditMode]);

  const toggle = (s) => setSections(p => ({ ...p, [s]: !p[s] }));
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) { toast.error(language === 'ar' ? 'الحد الأقصى 10 صور' : 'Max 10 images'); return; }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setImages(prev => [...prev, { file, preview: ev.target.result }]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setImages(p => p.filter((_, i) => i !== idx));
    if (mainImageIndex === idx) setMainImageIndex(0);
    else if (mainImageIndex > idx) setMainImageIndex(p => p - 1);
  };

  const handleSubmit = async () => {
    if (!form.title) { toast.error(language === 'ar' ? 'يرجى إدخال عنوان العقار' : 'Please enter property title'); return; }
    // iter-218: require a price OR "contact for price"
    const _needPriceFor = form.listingType === 'for_rent' ? form.rentPrice : form.price;
    if (!form.priceOnRequest && (!_needPriceFor || parseFloat(_needPriceFor) <= 0)) {
      toast.error(language === 'ar' ? 'يرجى إدخال السعر أو اختيار "اتصل لمعرفة السعر"' : 'Please enter price or choose "Call for price"');
      return;
    }
    // Only require images for new properties, not edits
    if (!isEditMode && images.length === 0) { 
      toast.error(language === 'ar' ? 'يرجى إضافة صورة واحدة على الأقل' : 'Please add at least one image'); 
      return; 
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('business_token');
      const data = new FormData();
      
      if (!isEditMode) {
        data.append('storeType', 'real_estate');
      }
      
      Object.keys(form).forEach(k => {
        if (k === 'amenities') data.append(k, JSON.stringify(form[k]));
        else if (k === 'priceOnRequest') data.append(k, form[k] ? 'true' : 'false');
        else if (k === 'price' || k === 'rentPrice') { /* handled below */ }
        else if (form[k] !== '' && form[k] !== null) data.append(k, form[k]);
      });
      
      // Handle existing images (URLs) vs new images (files)
      const existingUrls = [];
      let newImageIndex = 0;
      images.forEach((img, idx) => {
        if (img.isExisting) {
          existingUrls.push(img.preview);
        } else if (img.file) {
          // Send actual File object, not base64 preview
          if (idx === mainImageIndex) {
            data.append('mainImage', img.file, img.file.name);
          } else {
            data.append(`extraImage${newImageIndex + 1}`, img.file, img.file.name);
            newImageIndex++;
          }
        }
      });
      
      // For real estate, also send the main image under 'image' field (backend expects this)
      const mainImg = images[mainImageIndex];
      if (mainImg && mainImg.file) {
        data.append('image', mainImg.file, mainImg.file.name);
      }
      
      if (existingUrls.length > 0) {
        data.append('existingImages', JSON.stringify(existingUrls));
      }
      
      const _rawPrice = form.listingType === 'for_rent' ? form.rentPrice : form.price;
      data.append('price', form.priceOnRequest ? 0 : (_rawPrice || 0));
      if (form.listingType === 'for_rent' && !form.priceOnRequest) data.append('rent_price', form.rentPrice || 0);
      
      // Add map coordinates
      if (form.mapLat) data.append('latitude', form.mapLat);
      if (form.mapLng) data.append('longitude', form.mapLng);
      if (form.locationAddress) data.append('location', form.locationAddress);
      
      if (isEditMode && editItem) {
        // PUT request for update
        await axios.put(`${BACKEND_URL}/api/stores/${store.id}/items/${editItem.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'ar' ? 'تم تحديث العقار بنجاح' : 'Property updated successfully');
      } else {
        // POST request for create
        await axios.post(`${BACKEND_URL}/api/stores/${store.id}/items`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'ar' ? 'تم إضافة العقار بنجاح' : 'Property added successfully');
      }
      
      onSuccess?.();
      onBack?.();
    } catch (error) {
      console.error('Error saving property:', error);
      let errorMsg = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      console.error('Error details:', errorMsg);
      toast.error(language === 'ar' ? `حدث خطأ: ${errorMsg}` : `Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const cls = isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : 'bg-white border-gray-200';
  const labelCls = `block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-[#1E9BD9]">
            <ArrowLeft className="h-5 w-5" />
            <span>{language === 'ar' ? 'رجوع' : 'Back'}</span>
          </button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {isEditMode 
              ? (language === 'ar' ? 'تعديل العقار' : 'Edit Property')
              : (language === 'ar' ? 'إضافة عقار جديد' : 'Add New Property')
            }
          </h1>
          <div className="w-20"></div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Images */}
        <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <h3 className={`font-bold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            <Image className="h-5 w-5 text-[#1E9BD9]" />
            {language === 'ar' ? `صور العقار (${images.length}/10)` : `Property Images (${images.length}/10)`}
          </h3>
          <div className="grid grid-cols-5 gap-3">
            {images.map((img, idx) => (
              <div key={idx} className="relative aspect-square group">
                <img src={img.preview} alt="" className={`w-full h-full object-cover rounded-xl ${mainImageIndex === idx ? 'ring-3 ring-[#1E9BD9]' : ''}`} />
                <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button>
                {mainImageIndex === idx && <div className="absolute bottom-1 left-1 bg-[#1E9BD9] text-white text-xs px-2 py-0.5 rounded">{language === 'ar' ? 'رئيسية' : 'Main'}</div>}
                {mainImageIndex !== idx && <button onClick={() => setMainImageIndex(idx)} className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100">{language === 'ar' ? 'تعيين' : 'Set'}</button>}
              </div>
            ))}
            {images.length < 10 && (
              <label className={`aspect-square border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer ${isDarkMode ? 'border-white/20 hover:border-[#1E9BD9]' : 'border-gray-200 hover:border-[#1E9BD9]'}`}>
                <input type="file" ref={fileInputRef} accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                <Plus className={`h-8 w-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{language === 'ar' ? 'إضافة' : 'Add'}</span>
              </label>
            )}
          </div>
        </div>

        {/* Basic Info */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('basic')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><Home className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'المعلومات الأساسية' : 'Basic Info'}</span></div>
            {sections.basic ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.basic && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {['for_sale', 'for_rent', 'for_investment'].map(type => (
                  <button key={type} onClick={() => update('listingType', type)} className={`py-3 px-4 rounded-xl font-medium ${form.listingType === type ? 'bg-[#1E9BD9] text-white' : isDarkMode ? 'bg-[#0F172A] text-white' : 'bg-gray-100'}`}>
                    {type === 'for_sale' ? (language === 'ar' ? 'للبيع' : 'For Sale') : type === 'for_rent' ? (language === 'ar' ? 'للإيجار' : 'For Rent') : (language === 'ar' ? 'للاستثمار' : 'Investment')}
                  </button>
                ))}
              </div>
              <div><label className={labelCls}>{language === 'ar' ? 'عنوان العقار' : 'Title'} *</label><input value={form.title} onChange={(e) => update('title', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder={language === 'ar' ? 'مثال: شقة فاخرة في دبي' : 'e.g., Luxury Apartment'} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'نوع العقار' : 'Type'}</label><select value={form.propertyType} onChange={(e) => update('propertyType', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}>{PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{language === 'ar' ? t.label : t.labelEn}</option>)}</select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الإمارة' : 'Emirate'}</label><select value={form.emirate} onChange={(e) => update('emirate', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}>{EMIRATES.map(e => <option key={e.value} value={e.value}>{language === 'ar' ? e.label : e.labelEn}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'المنطقة' : 'Community'}</label><input value={form.community} onChange={(e) => update('community', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'المنطقة الفرعية' : 'Sub-Community'}</label><input value={form.subCommunity} onChange={(e) => update('subCommunity', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
              </div>
              {form.listingType === 'for_sale' && (
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'السعر (درهم)' : 'Price (AED)'} *</label>
                  <PriceModePicker
                    value={form.priceOnRequest ? 'onRequest' : 'fixed'}
                    onChange={(m) => update('priceOnRequest', m === 'onRequest')}
                    language={language}
                    isDarkMode={isDarkMode}
                    testIdPrefix="property"
                  />
                  {!form.priceOnRequest && (
                    <input type="number" inputMode="decimal" value={form.price} onChange={(e) => update('price', e.target.value)} className={`w-full mt-2 px-3 py-2.5 border rounded-lg ${cls}`} data-testid="property-price-input" />
                  )}
                </div>
              )}
              {form.listingType === 'for_rent' && (
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'الإيجار السنوي' : 'Yearly Rent'} *</label>
                  <PriceModePicker
                    value={form.priceOnRequest ? 'onRequest' : 'fixed'}
                    onChange={(m) => update('priceOnRequest', m === 'onRequest')}
                    language={language}
                    isDarkMode={isDarkMode}
                    testIdPrefix="property-rent"
                  />
                  {!form.priceOnRequest && (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div><input type="number" inputMode="decimal" value={form.rentPrice} onChange={(e) => update('rentPrice', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder={language === 'ar' ? 'الإيجار السنوي' : 'Yearly rent'} /></div>
                      <div><label className={labelCls}>{language === 'ar' ? 'عدد الشيكات' : 'Cheques'}</label><select value={form.cheques} onChange={(e) => update('cheques', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="1">1</option><option value="2">2</option><option value="4">4</option><option value="6">6</option><option value="12">12</option></select></div>
                    </div>
                  )}
                </div>
              )}
              {form.listingType === 'for_investment' && (
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'سعر العقار' : 'Price'} *</label>
                  <PriceModePicker
                    value={form.priceOnRequest ? 'onRequest' : 'fixed'}
                    onChange={(m) => update('priceOnRequest', m === 'onRequest')}
                    language={language}
                    isDarkMode={isDarkMode}
                    testIdPrefix="property-inv"
                  />
                  {!form.priceOnRequest && (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div><input type="number" inputMode="decimal" value={form.price} onChange={(e) => update('price', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
                      <div><label className={labelCls}>{language === 'ar' ? 'العائد المتوقع %' : 'Expected ROI %'}</label><input type="number" inputMode="decimal" value={form.expectedRoi} onChange={(e) => update('expectedRoi', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder="8" /></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Details */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('details')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><Maximize className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'تفاصيل العقار' : 'Property Details'}</span></div>
            {sections.details ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.details && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'غرف النوم' : 'Bedrooms'}</label><select value={form.bedrooms} onChange={(e) => update('bedrooms', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="">-</option><option value="studio">Studio</option>{[1,2,3,4,5,6,'7+'].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الحمامات' : 'Bathrooms'}</label><select value={form.bathrooms} onChange={(e) => update('bathrooms', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="">-</option>{[1,2,3,4,5,'6+'].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'المساحة (قدم)' : 'Area (sqft)'}</label><input type="number" inputMode="decimal" value={form.area} onChange={(e) => update('area', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'مساحة الأرض' : 'Plot Area'}</label><input type="number" inputMode="decimal" value={form.plotArea} onChange={(e) => update('plotArea', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'المواقف' : 'Parking'}</label><select value={form.parking} onChange={(e) => update('parking', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="">-</option>{[0,1,2,3,'4+'].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'التأثيث' : 'Furnished'}</label><select value={form.furnished} onChange={(e) => update('furnished', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="furnished">{language === 'ar' ? 'مفروش' : 'Furnished'}</option><option value="semi_furnished">{language === 'ar' ? 'مفروش جزئياً' : 'Semi-Furnished'}</option><option value="unfurnished">{language === 'ar' ? 'غير مفروش' : 'Unfurnished'}</option></select></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الحالة' : 'Status'}</label><select value={form.status} onChange={(e) => update('status', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="vacant">{language === 'ar' ? 'شاغر' : 'Vacant'}</option><option value="occupied">{language === 'ar' ? 'مؤجر' : 'Occupied'}</option></select></div>
              </div>
              <div>
                <label className={labelCls}>{language === 'ar' ? 'حالة الإنجاز' : 'Completion Status'}</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ v: 'ready', ar: 'جاهز', en: 'Ready' }, { v: 'offplan', ar: 'على الخارطة', en: 'Off-plan' }].map(o => (
                    <button key={o.v} type="button" onClick={() => update('completion', o.v)} data-testid={`completion-${o.v}`}
                      className={`p-3 rounded-xl border text-sm font-medium transition-all ${form.completion === o.v ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]' : isDarkMode ? 'border-white/10 text-gray-300' : 'border-gray-200'}`}>
                      {language === 'ar' ? o.ar : o.en}
                    </button>
                  ))}
                </div>
              </div>
              <div><label className={labelCls}>{language === 'ar' ? 'الوصف' : 'Description'}</label><textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={4} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder={language === 'ar' ? 'أدخل وصف تفصيلي...' : 'Enter detailed description...'} /></div>
            </div>
          )}
        </div>

        {/* Building Info */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('building')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><Building2 className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'معلومات المبنى' : 'Building Info'}</span></div>
            {sections.building ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.building && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'اسم المبنى' : 'Building Name'}</label><input value={form.buildingName} onChange={(e) => update('buildingName', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'المطور' : 'Developer'}</label><input value={form.developer} onChange={(e) => update('developer', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelCls}>{language === 'ar' ? 'عدد الطوابق' : 'Total Floors'}</label><input type="number" inputMode="decimal" value={form.totalFloors} onChange={(e) => update('totalFloors', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'الطابق' : 'Floor'}</label><input value={form.floor} onChange={(e) => update('floor', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} /></div>
                <div><label className={labelCls}>{language === 'ar' ? 'نوع التملك' : 'Ownership'}</label><select value={form.ownership} onChange={(e) => update('ownership', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`}><option value="freehold">{language === 'ar' ? 'تملك حر' : 'Freehold'}</option><option value="leasehold">{language === 'ar' ? 'إيجار طويل' : 'Leasehold'}</option></select></div>
              </div>
            </div>
          )}
        </div>

        {/* Amenities */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('amenities')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><Check className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'المرافق والخدمات' : 'Amenities'}</span></div>
            {sections.amenities ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.amenities && (
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {PROPERTY_AMENITIES.map(a => (
                  <button key={a.id} onClick={() => update('amenities', form.amenities.includes(a.id) ? form.amenities.filter(x => x !== a.id) : [...form.amenities, a.id])}
                    className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${form.amenities.includes(a.id) ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]' : isDarkMode ? 'border-white/10 text-gray-300' : 'border-gray-200'}`}>
                    <span>{a.icon}</span><span className="text-sm">{language === 'ar' ? a.label : a.labelEn}</span>{form.amenities.includes(a.id) && <Check className="h-4 w-4 ms-auto" />}
                  </button>
                ))}
              </div>
              <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                <p className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? '🎥 الجولات الافتراضية (اختياري)' : '🎥 Virtual Viewings (optional)'}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><label className={labelCls}>{language === 'ar' ? 'رابط جولة فيديو' : 'Video Tour URL'}</label><input type="url" inputMode="url" dir="ltr" value={form.videoTourUrl} onChange={(e) => update('videoTourUrl', e.target.value)} placeholder="https://youtube.com/..." className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} data-testid="video-tour-input" /></div>
                  <div><label className={labelCls}>{language === 'ar' ? 'رابط جولة 360°' : '360° Tour URL'}</label><input type="url" inputMode="url" dir="ltr" value={form.tour360Url} onChange={(e) => update('tour360Url', e.target.value)} placeholder="https://..." className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} data-testid="tour-360-input" /></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Location */}
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
          <button onClick={() => toggle('location')} className={`w-full flex items-center justify-between p-4 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3"><MapPin className="h-5 w-5 text-[#1E9BD9]" /><span className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'الموقع على الخريطة' : 'Map Location'}</span></div>
            {sections.location ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {sections.location && (
            <div className="p-4 space-y-4">
              <div><label className={labelCls}>{language === 'ar' ? 'العنوان التفصيلي' : 'Full Address'}</label><input value={form.locationAddress} onChange={(e) => update('locationAddress', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder={language === 'ar' ? 'مثال: برج X، شارع Y' : 'e.g., Tower X, Street Y'} /></div>
              
              {/* Interactive Google Map */}
              <GoogleMapPicker
                isDarkMode={isDarkMode}
                language={language}
                initialLocation={form.mapLat && form.mapLng ? {
                  lat: parseFloat(form.mapLat),
                  lng: parseFloat(form.mapLng)
                } : null}
                onLocationSelect={(locationData) => {
                  if (locationData) {
                    update('mapLat', locationData.lat.toString());
                    update('mapLng', locationData.lng.toString());
                    if (locationData.address && !form.locationAddress) {
                      update('locationAddress', locationData.address);
                    }
                  } else {
                    update('mapLat', '');
                    update('mapLng', '');
                  }
                }}
              />
              
              {/* Manual Coordinates (hidden by default, show only if needed) */}
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>Latitude</label><input value={form.mapLat} onChange={(e) => update('mapLat', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder="25.2048" /></div>
                <div><label className={labelCls}>Longitude</label><input value={form.mapLng} onChange={(e) => update('mapLng', e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg ${cls}`} placeholder="55.2708" /></div>
              </div>
            </div>
          )}
        </div>

        {/* Submit — inline at end of form (matches Add Plate/Phone pattern) */}
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="w-full h-12 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90 text-white rounded-xl text-base font-bold disabled:opacity-50"
          data-testid="add-property-submit-btn"
        >
          {saving 
            ? <><Loader2 className="h-5 w-5 animate-spin me-2" />{language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</>
            : isEditMode 
              ? (language === 'ar' ? 'حفظ التعديلات' : 'Save Changes')
              : (language === 'ar' ? 'إضافة العقار' : 'Add Property')
          }
        </Button>
      </div>
    </div>
  );
}
